const PASSOS_POR_INTERVALO = [
  { ate: 5, passo: 3 },
  { ate: 25, passo: 2 },
  { ate: Infinity, passo: 1 },
];

export function obterPassoCurvaMestra(intervalo) {
  const intervaloNumerico = Math.max(1, Number(intervalo) || 1);
  return PASSOS_POR_INTERVALO.find(regra => intervaloNumerico <= regra.ate).passo;
}

export function obterNiveisCurvasMestras(curvas, intervalo) {
  const passo = obterPassoCurvaMestra(intervalo);
  return [...new Set((curvas || [])
    .map(curva => Number(curva?.nivel))
    .filter(Number.isFinite))]
    .sort((a, b) => a - b)
    .filter((_, indice) => indice % passo === 0);
}

function pontoMedioDoSegmento(curva) {
  const [inicio, fim] = curva.positions || [];
  if (!inicio || !fim) return null;
  const latitude = (Number(inicio[0]) + Number(fim[0])) / 2;
  const longitude = (Number(inicio[1]) + Number(fim[1])) / 2;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  const deltaX = (Number(fim[1]) - Number(inicio[1])) * Math.cos(latitude * Math.PI / 180);
  const deltaY = -(Number(fim[0]) - Number(inicio[0]));
  let angulo = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
  if (angulo > 90) angulo -= 180;
  if (angulo < -90) angulo += 180;

  return { position: [latitude, longitude], angle: angulo };
}

function limitesDosPontos(pontos) {
  const latitudes = pontos.map(ponto => ponto.position[0]);
  const longitudes = pontos.map(ponto => ponto.position[1]);
  return {
    minLat: Math.min(...latitudes),
    maxLat: Math.max(...latitudes),
    minLng: Math.min(...longitudes),
    maxLng: Math.max(...longitudes),
  };
}

function distanciaNormalizada(a, b, limites) {
  const amplitudeLat = Math.max(limites.maxLat - limites.minLat, 0.000001);
  const amplitudeLng = Math.max(limites.maxLng - limites.minLng, 0.000001);
  const deltaLat = (a.position[0] - b.position[0]) / amplitudeLat;
  const deltaLng = (a.position[1] - b.position[1]) / amplitudeLng;
  return Math.hypot(deltaLat, deltaLng);
}

function parametrosDeDensidade(zoom) {
  const zoomNumerico = Number(zoom) || 13;
  if (zoomNumerico <= 11) return { distanciaMinima: 0.16, maximo: 8 };
  if (zoomNumerico <= 13) return { distanciaMinima: 0.11, maximo: 12 };
  if (zoomNumerico <= 15) return { distanciaMinima: 0.075, maximo: 20 };
  return { distanciaMinima: 0.045, maximo: 32 };
}

export function selecionarRotulosCurvas(curvas, intervalo, zoom) {
  const grupos = new Map();
  (curvas || []).forEach(curva => {
    const ponto = pontoMedioDoSegmento(curva);
    if (!ponto) return;
    const chave = Number(curva.nivel);
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(ponto);
  });

  const niveis = obterNiveisCurvasMestras(curvas, intervalo).filter(nivel => grupos.has(nivel));
  const candidatos = niveis.flatMap(nivel => grupos.get(nivel));
  if (!candidatos.length) return [];

  const limites = limitesDosPontos(candidatos);
  const { distanciaMinima, maximo } = parametrosDeDensidade(zoom);
  const selecionados = [];

  niveis.forEach((nivel, indiceNivel) => {
    if (selecionados.length >= maximo) return;
    const pontosDoNivel = grupos.get(nivel);
    const alvo = ((indiceNivel * 0.381966) + 0.5) % 1;
    const candidatosOrdenados = pontosDoNivel
      .map((ponto, indice) => ({
        ...ponto,
        ordem: Math.abs((indice / Math.max(1, pontosDoNivel.length - 1)) - alvo),
      }))
      .sort((a, b) => a.ordem - b.ordem);
    const escolhido = candidatosOrdenados.find(candidato => selecionados.every(
      selecionado => distanciaNormalizada(candidato, selecionado, limites) >= distanciaMinima,
    ));
    if (escolhido) selecionados.push({
      id: `${nivel}-${indiceNivel}`,
      nivel,
      position: escolhido.position,
      angle: escolhido.angle,
    });
  });

  return selecionados;
}
